import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  ButtonBase,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
  Videocam as VideocamIcon,
  Delete as DeleteIcon,
  PlayCircleOutline as PlayIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";

const SCORE_OPTIONS = [-1, 0, 1, 2, 3, 4, 5];

function EmojiIcon({ emoji, size = 28 }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: size,
        lineHeight: 1,
        fontFamily:
          '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Android Emoji","EmojiSymbols",sans-serif',
      }}
    >
      {emoji}
    </Box>
  );
}

function ScorePicker({ value, onChange }) {
  const emojiByScore = {
    [-1]: "🤬",
    [0]: "😡",
    [1]: "😐",
    [2]: "🙂",
    [3]: "😀",
    [4]: "😁",
    [5]: "🤩",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${SCORE_OPTIONS.length}, minmax(0, 1fr))`,
          alignItems: "center",
          justifyItems: "center",
          columnGap: 0.75,
        }}
      >
        {SCORE_OPTIONS.map((score) => {
          const selected = value === score;
          const emoji = emojiByScore[score] || "😐";
          return (
            <ButtonBase
              key={score}
              onClick={() => onChange(score)}
              sx={{
                border: "1px solid",
                borderColor: selected ? "#6D28D9" : "#E5E7EB",
                bgcolor: selected ? "rgba(109, 40, 217, 0.10)" : "#fff",
                width: 46,
                height: 46,
                p: 0,
                borderRadius: "50%",
                WebkitTapHighlightColor: "transparent",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": { bgcolor: selected ? "rgba(109, 40, 217, 0.16)" : "#F9FAFB" },
                "&.Mui-focusVisible": {
                  bgcolor: selected ? "rgba(109, 40, 217, 0.16)" : "#F9FAFB",
                  outline: "none",
                },
                "&:focus": { outline: "none" },
              }}
            >
              <EmojiIcon emoji={emoji} size={28} />
            </ButtonBase>
          );
        })}
      </Box>

      <Box sx={{ mt: 0.75, px: 1.5 }}>
        <Box sx={{ height: "1px", bgcolor: "#D1D5DB" }} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${SCORE_OPTIONS.length}, minmax(0, 1fr))`,
          alignItems: "start",
          justifyItems: "center",
          columnGap: 0.75,
          mt: 0.25,
        }}
      >
        {SCORE_OPTIONS.map((score) => {
          const selected = value === score;
          return (
            <Box key={score} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ width: "1px", height: 7, bgcolor: "#D1D5DB" }} />
              <Typography
                variant="caption"
                sx={{ mt: 0.4, color: selected ? "#111827" : "#4B5563", fontWeight: selected ? 700 : 500 }}
              >
                {score}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

async function getVideoDurationSeconds(file) {
  const url = URL.createObjectURL(file);
  try {
    const duration = await new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      // Some browsers need an explicit load() for metadata.
      // Keep it silent/inert; we only need duration.
      video.onloadedmetadata = () => resolve(video.duration);
      video.onerror = () => reject(new Error("failed to load video metadata"));
      video.src = url;
      try {
        video.load();
      } catch {
        // ignore
      }
    });
    return duration;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function isProbablyVideo(file) {
  if (file?.type?.startsWith("video/")) return true;
  const name = (file?.name || "").toLowerCase();
  return /\.(mp4|mov|webm|mkv|m4v|avi)$/i.test(name);
}

function isProbablyImage(file) {
  if (file?.type?.startsWith("image/")) return true;
  const name = (file?.name || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(name);
}

async function filterAndNormalizeFiles(files, { minVideoDurationSeconds = 10, maxVideoDurationSeconds = 20 } = {}) {
  const out = [];
  let videoDuration = null;
  let rejectionReason = null;

  for (const file of files) {
    if (isProbablyImage(file)) {
      out.push(file);
      continue;
    }
    if (isProbablyVideo(file)) {
      try {
        const duration = await getVideoDurationSeconds(file);
        if (duration < minVideoDurationSeconds) {
          rejectionReason = `video must be at least ${minVideoDurationSeconds} seconds`;
          continue;
        }
        if (duration > maxVideoDurationSeconds) {
          rejectionReason = `video must not exceed ${maxVideoDurationSeconds} seconds`;
          continue;
        }
        out.push(file);
        videoDuration = Math.round(duration);
      } catch {
        // if metadata can't be read, allow the video to avoid dead-ends
        out.push(file);
      }
      continue;
    }
  }
  return { files: out, videoDuration, rejectionReason };
}

function FeedbackField({
  value,
  onChange,
  placeholder,
  minVideoDurationSeconds = 10,
  maxVideoDurationSeconds = 20,
  helperText,
}) {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [fileError, setFileError] = useState("");

  // Build object-URL previews for local File objects; track them to revoke on unmount
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const mediaFiles = value.mediaFiles || [];
    const urls = mediaFiles.map((file) =>
      file instanceof File ? URL.createObjectURL(file) : null
    );
    setPreviews(urls);
    return () => {
      urls.forEach((url) => { if (url) URL.revokeObjectURL(url); });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mediaFiles]);

  const handleAdd = async (files) => {
    const { files: normalized, videoDuration, rejectionReason } = await filterAndNormalizeFiles(files, {
      minVideoDurationSeconds,
      maxVideoDurationSeconds,
    });
    setFileError(rejectionReason || "");
    if (!normalized.length) return;
    const next = { ...value, mediaFiles: [...(value.mediaFiles || []), ...normalized] };
    if (videoDuration !== null) next.videoDuration = videoDuration;
    onChange(next);
  };

  const handleRemoveFile = (idx) => {
    onChange({
      ...value,
      mediaFiles: (value.mediaFiles || []).filter((_, i) => i !== idx),
    });
  };

  // Existing uploaded URLs from the API (mediaUrls)
  const existingUrls = value.mediaUrls || [];

  const handleRemoveExisting = (idx) => {
    const next = existingUrls.filter((_, i) => i !== idx);
    onChange({ ...value, mediaUrls: next });
  };

  return (
    <Box>
      <TextField
        value={value.comment}
        onChange={(e) =>
          onChange({ ...value, comment: (e.target.value || "").slice(0, 500) })
        }
        placeholder={placeholder}
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
        inputProps={{ maxLength: 500 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  await handleAdd(files);
                }}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  await handleAdd(files);
                }}
              />
              <IconButton
                size="small"
                onClick={() => imageInputRef.current?.click()}
                title="add photo"
              >
                <PhotoCameraIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => videoInputRef.current?.click()}
                title="add video"
              >
                <VideocamIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {!!helperText && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#6B7280" }}>
          {helperText}
        </Typography>
      )}
      {!!fileError && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#DC2626" }}>
          {fileError}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "#9CA3AF" }}>
        {(value?.comment || "").length}/500
      </Typography>

      {/* Unified thumbnail row — existing uploaded URLs + newly selected local files together */}
      {(existingUrls.length > 0 || (value.mediaFiles || []).length > 0) && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* Already-uploaded URLs from API (mediaUrls) */}
          {existingUrls.map((url, idx) => {
            const isVideo = /\.(mp4|mov|webm|mkv|m4v|avi)(\?|$)/i.test(url);
            return (
              <Box
                key={`existing-${idx}`}
                sx={{
                  position: "relative",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  width: 64,
                  height: 64,
                  flexShrink: 0,
                  bgcolor: "#F3F4F6",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                {isVideo ? (
                  <>
                    <video
                      src={url}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      muted
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "rgba(0,0,0,0.55)",
                        borderRadius: "50%",
                        p: 0.3,
                        display: "flex",
                      }}
                    >
                      <PlayIcon sx={{ fontSize: 16, color: "#fff" }} />
                    </Box>
                  </>
                ) : (
                  <img
                    src={url}
                    alt={`uploaded-${idx}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveExisting(idx)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "rgba(255,255,255,0.85)",
                    p: 0.2,
                    "&:hover": { bgcolor: "#ffebee", color: "#d32f2f" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            );
          })}

          {/* Newly selected local files */}
          {(value.mediaFiles || []).map((file, idx) => {
            const isVideo = isProbablyVideo(file);
            const previewUrl = previews[idx];
            return (
              <Box
                key={`${file.name}-${idx}`}
                sx={{
                  position: "relative",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  width: 64,
                  height: 64,
                  flexShrink: 0,
                  bgcolor: "#F3F4F6",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                {isVideo ? (
                  <>
                    <video
                      src={previewUrl}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      muted
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "rgba(0,0,0,0.55)",
                        borderRadius: "50%",
                        p: 0.3,
                        display: "flex",
                      }}
                    >
                      <PlayIcon sx={{ fontSize: 16, color: "#fff" }} />
                    </Box>
                  </>
                ) : (
                  <img
                    src={previewUrl}
                    alt={file.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(idx)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "rgba(255,255,255,0.85)",
                    p: 0.2,
                    "&:hover": { bgcolor: "#ffebee", color: "#d32f2f" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function TeamMemberRatingBlock({
  index,
  teamMembers,
  value,
  onChange,
  onRemoveBlock,
  canRemove,
}) {
  const selectedMember = teamMembers.find((m) => String(m.value) === String(value.teamMemberId)) || null;

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2,
        p: 2,
        mt: index === 0 ? 0 : 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="subtitle2" sx={{ color: "#111827", fontWeight: 700 }}>
          rate team member
        </Typography>
        {canRemove && (
          <Button
            size="small"
            variant="text"
            onClick={onRemoveBlock}
            sx={{ textTransform: "none", color: "#fff" }}
          >
            remove
          </Button>
        )}
      </Box>

      <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mt: 0.5 }}>
        select team member
      </Typography>

      <Select
        size="small"
        value={value.teamMemberId || ""}
        displayEmpty
        onChange={(e) => onChange({ ...value, teamMemberId: e.target.value })}
        sx={{ mt: 1, width: "100%", borderRadius: 2 }}
        renderValue={(val) => {
          if (!val) return <Typography sx={{ color: "#9CA3AF" }}>select</Typography>;
          if (!selectedMember) return String(val);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar src={selectedMember.avatar} sx={{ width: 22, height: 22 }}>
                {selectedMember.label?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography>{selectedMember.label}</Typography>
            </Box>
          );
        }}
      >
        <MenuItem value="" disabled>
          select
        </MenuItem>
        {teamMembers.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar src={m.avatar} sx={{ width: 22, height: 22 }}>
                {m.label?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography>{m.label}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>

      <Box sx={{ mt: 2 }}>
        <ScorePicker value={value.score} onChange={(score) => onChange({ ...value, score })} />
      </Box>

      <FeedbackField
        value={value}
        onChange={onChange}
        placeholder="add comments, pic or a video to share your feedback"
        maxVideoDurationSeconds={20}
        helperText="video must be 10–20 sec"
      />
    </Box>
  );
}

export default function RatingDialog({
  open,
  onClose,
  teamMembers = [],
  value,
  onChange,
  onSubmit,
  isSubmitting = false,
  validationMessage = "",
}) {
  const canSubmit = useMemo(() => {
    const hasPlaceScore = typeof value?.place?.score === "number";
    const hasTeamScore = (value?.teamMemberRatings || []).some((r) => typeof r.score === "number");
    return hasPlaceScore || hasTeamScore;
  }, [value]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, textTransform: "lowercase" }}>
              rating
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
              please rate team member based on your experience and the service provided
            </Typography>
            <Typography variant="body2" sx={{ color: "#6D28D9", mt: 0.5, fontWeight: 600 }}>
              you can rate more than one team member
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          {(() => {
            const selectedIds = (value?.teamMemberRatings || [])
              .map((r) => r.teamMemberId)
              .filter(Boolean);

            return (value?.teamMemberRatings || []).map((block, idx) => (
              <TeamMemberRatingBlock
                key={block.clientId}
                index={idx}
                teamMembers={teamMembers.filter(
                  (m) => !selectedIds.includes(m.value) || m.value === block.teamMemberId
                )}
                value={block}
                onChange={(next) => {
                  const nextBlocks = [...value.teamMemberRatings];
                  nextBlocks[idx] = next;
                  onChange({ ...value, teamMemberRatings: nextBlocks });
                }}
                onRemoveBlock={() => {
                  const nextBlocks = value.teamMemberRatings.filter((_, i) => i !== idx);
                  onChange({ ...value, teamMemberRatings: nextBlocks });
                }}
                canRemove={(value?.teamMemberRatings || []).length > 1}
              />
            ));
          })()}

          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => {
              const nextBlocks = [
                ...(value.teamMemberRatings || []),
                {
                  clientId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                  teamMemberId: "",
                  score: null,
                  comment: "",
                  mediaFiles: [],
                },
              ];
              onChange({ ...value, teamMemberRatings: nextBlocks });
            }}
            sx={{ mt: 1, textTransform: "none", color: "#fff", fontWeight: 700 }}
          >
            rate additional team members
          </Button>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: "lowercase" }}>
              rate place
            </Typography>
          </Box>

          <Box sx={{ mt: 1 }}>
            <ScorePicker
              value={value?.place?.score ?? null}
              onChange={(score) => onChange({ ...value, place: { ...value.place, score } })}
            />
          </Box>

          <FeedbackField
            value={value.place}
            onChange={(place) => onChange({ ...value, place })}
            placeholder="add comments, pic or a video to share your feedback"
            maxVideoDurationSeconds={20}
            helperText="video must be 10–20 sec"
          />
        </Box>

        <Typography variant="body2" sx={{ color: "#6B7280", mt: 2, textTransform: "lowercase" }}>
          advance rating
        </Typography>

        <Box sx={{ mt: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting || Boolean(validationMessage)}
            sx={{
              bgcolor: "#6D28D9",
              borderRadius: 999,
              textTransform: "lowercase",
              fontWeight: 800,
              py: 1.25,
              "&:hover": { bgcolor: "#5B21B6" },
            }}
          >
            {isSubmitting ? "submitting..." : "submit"}
          </Button>
        </Box>
        {!!validationMessage && (
          <Typography variant="caption" sx={{ mt: 1, color: "#DC2626", display: "block" }}>
            {validationMessage}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

